import Vue from 'vue';
import Component from 'vue-class-component';
import {DataManager, Visualisation, Toolbar, Chart, FilterBar} from 'wc-visualisation';
import {Table, Icon, Popover, FieldType} from 'wc-lib';
import * as screenfull from 'screenfull';
import { NemDispatchOverviewRegion } from './dispatch-overview-region';
import { NemDispatchOverviewInterconnector } from './dispatch-overview-interconnector';

require('wc-lib/icons/fullscreen.svg');

@Component({
  template: require('./dispatch-overview.html'),
  name: 'dispatch-overview',
  components: {
    'vis-visualisation': Visualisation,
    'vis-toolbar': Toolbar,
    'wc-icon': Icon,
    'wc-table': Table,
    'vis-chart': Chart,
    'vis-filter-bar': FilterBar,
    'wc-popover': Popover,
    'dispatch-overview-region': NemDispatchOverviewRegion,
    'dispatch-overview-interconnector': NemDispatchOverviewInterconnector
  }
})
export class NemDispatchOverview extends Vue {
  isFullscreen = false;
  regions: Array<any> = [];
  prices: Array<any> = [];
  intervalRefresh: any;
  settlementDate = null;
  pricesColumns = [
    { title: 'Price', field: 'price' },
    { title: 'QLD', field: 'QLD1', fieldType: FieldType.Dollar },
    { title: 'NSW', field: 'NSW1', fieldType: FieldType.Dollar },
    { title: 'SA', field: 'SA1', fieldType: FieldType.Dollar },
    { title: 'VIC', field: 'VIC1', fieldType: FieldType.Dollar },
    { title: 'TAS', field: 'TAS1', fieldType: FieldType.Dollar }
  ];

  created() {
    if (screenfull.isEnabled) {
      (screenfull as any).onchange(() => {
        this.isFullscreen = (screenfull as any).isFullscreen;
      });
    }
    this.fetchData();
    this.intervalRefresh = setInterval(() => {
      this.fetchData();
    }, 10000);
  }



  async fetchData() {
    let regions = [];
    let prices = [];
    const id = await DataManager.addEndPoint('../api/report/ELEC_NEM_SUMMARY', undefined, 60000, undefined, undefined, 'yyyy-MM-ddTHH:mm:ss', ['SETTLEMENTDATE']);   
    const dynamicView = DataManager.getDynamicView(id, 'dispatchoverview');
    const data = dynamicView.data();
    // Line 71 Number.EPSILON & Line 76
    // Code below is required due to region price values of '-0.0000x' displayed as 0$.00, issue is caused by 'numeral' library.
    // EPSILON is required as values 1.005 are rounded as 1.00,this should be 1.01.
    // This may no longer be required if numeral is fixed or replaced. 
    var eps = Number.EPSILON || 2.220446049250313e-16;
    if (data.length === 1) {
      for (const region of data[0].ELEC_NEM_SUMMARY) {
        regions.push({
          netInterchange: region.NETINTERCHANGE,
          price: (region.PRICE + eps).toFixed(2),
          priceStatus: region.PRICE_STATUS,
          apcFlag: region.APCFLAG ?? 0,
          marketSuspendedFlag: region.MARKETSUSPENDEDFLAG ?? 0,
          regionId: region.REGIONID,
          scheduledGeneration: region.SCHEDULEDGENERATION,
          semiScheduledGeneration: region.SEMISCHEDULEDGENERATION,
          settlementDate: region.SETTLEMENTDATE,
          totalDemand: region.TOTALDEMAND,
          interconnectorFlows: JSON.parse(region.INTERCONNECTORFLOWS)
        });
      }

      const dictionary = {
        RRP: { price: 'Energy' },
        RAISEREGRRP: { price: 'Raise Reg' },
        LOWERREGRRP: { price: 'Lower Reg' },
        RAISE1SECRRP: { price: 'Raise 1 sec' },
        RAISE6SECRRP: { price: 'Raise 6 sec' },
        RAISE60SECRRP: { price: 'Raise 60 sec' },
        RAISE5MINRRP: { price: 'Raise 5 min' },
        LOWER1SECRRP: { price: 'Lower 1 sec' },
        LOWER6SECRRP: { price: 'Lower 6 sec' },
        LOWER60SECRRP: { price: 'Lower 60 sec' },
        LOWER5MINRRP: { price: 'Lower 5 min' }
      };

      for (const region of data[0].ELEC_NEM_SUMMARY_PRICES) {
        dictionary['RRP'][region.REGIONID] = region.RRP;
        dictionary['RAISEREGRRP'][region.REGIONID] = region.RAISEREGRRP;
        dictionary['LOWERREGRRP'][region.REGIONID] = region.LOWERREGRRP;
        dictionary['RAISE1SECRRP'][region.REGIONID] = region.RAISE1SECRRP;
        dictionary['RAISE6SECRRP'][region.REGIONID] = region.RAISE6SECRRP;
        dictionary['RAISE60SECRRP'][region.REGIONID] = region.RAISE60SECRRP;
        dictionary['RAISE5MINRRP'][region.REGIONID] = region.RAISE5MINRRP;
        dictionary['LOWER1SECRRP'][region.REGIONID] = region.LOWER1SECRRP;
        dictionary['LOWER6SECRRP'][region.REGIONID] = region.LOWER6SECRRP;
        dictionary['LOWER60SECRRP'][region.REGIONID] = region.LOWER60SECRRP;
        dictionary['LOWER5MINRRP'][region.REGIONID] = region.LOWER5MINRRP;
      }

      for (const price in dictionary) prices.push(dictionary[price]);
    }

    if (regions.length > 0) {
      this.settlementDate = regions[0].settlementDate;
    }

    this.regions = regions;
    this.prices = prices;
  }

  getRegion(regionId: string) {
    return this.regions.find(x => x.regionId === regionId);
  }

  getInterconnector(region, interconnector: string) {
    if (region)
      return region.interconnectorFlows.find(x => x.name === interconnector);
  }

  toggleFullscreen() {
    (screenfull as any).toggle();
  }

  destroyed() {
    clearInterval(this.intervalRefresh)
  }
}

Vue.filter('interconnector', (region: any, interconnector: string) => {
  if (region)
    return region.interconnectorFlows.find(x => x.name === interconnector);
});



// WEBPACK FOOTER //
// src/views/electricity/nem/dispatch-overview/dispatch-overview.ts